import type { MarketingLanguage } from "../../marketing-content/marketingLocales";
import { getMarketingTranslations } from "../../marketing-content/marketingTranslations";
import type { LandingUploadFailureReason } from "./handoff/landingFileUpload";

export type LandingLanguage = MarketingLanguage;

// "unknown" covers a failure that is not a LandingUploadError (nothing told us the reason).
export type LandingUploadErrorMessageKey = LandingUploadFailureReason | "unknown";

/**
 * Every visitor-facing string of the production landing page (the Moss and Clearing designs), grouped
 * by the section that shows it. The marketing sub-pages reuse some of these sections, so they read
 * the same dictionary. Look a language's copy up with `useLandingCopy()` (LandingLanguageContext).
 *
 * Deliberately NOT in here: the USMLE Step 1 vignette in ExamStyleSection (that exam is taken in
 * English), university names in UniversityMarquee (official names), and the nav/footer link labels
 * (LandingNav and LandingFooter carry their own bilingual label maps).
 */
export type LandingCopy = {
  nav: {
    homeLinkAriaLabel: string;
    openMenuAriaLabel: string;
    closeMenuAriaLabel: string;
  };
  hero: {
    // English/Spanish keep their editorial desktop breaks. Other editions wrap
    // freely; Chinese/Japanese layout breaks join without added word spaces.
    // Do not copy English line lengths into other languages.
    headlineWithDesktopLineBreaks: string;
  };
  upload: {
    uploadSlidesButton: string;
    uploadingButtonLabel: string;
    preparingButtonLabel: string;
    processingButtonLabel: string;
    dropTitle: string;
    dropDescription: string;
    pasteLinkPlaceholder: string;
    noFilePrompt: string;
    signUpAndAddLater: string;
    pasteLinkInputAriaLabel: string;
    submitLinkButtonAriaLabel: string;
    errorMessageByFailureReason: Record<LandingUploadErrorMessageKey, string>;
  };
  exampleQuestion: {
    lectureSlideAlt: string;
    stem: string;
    // Same order in every language: the component marks the second option as the correct one.
    options: [string, string, string, string];
  };
  qualityChart: {
    heading: string;
    metricNames: {
      relevanceToYourDocument: string;
      easyToUnderstand: string;
      plausibleWrongAnswerChoices: string;
    };
    // Jungle first, then the four anonymised competitors, in the order the scores are listed.
    seriesNames: [string, string, string, string, string];
    singleToneCompetitorsLegend: string;
    competitorsFootnote: string;
    methodFootnote: string;
  };
  testimonials: {
    heading: string;
    leftTestimonial: { name: string; role: string; quote: string };
    rightTestimonial: { name: string; role: string; quote: string };
    studentMessageScreenshotAlt: string;
  };
  learnByTalking: {
    heading: string;
    callMonkeyTutorLine: string;
    monkeyCallsYouLine: string;
    callScreen: {
      tutorName: string;
      learnerRequestBubble: string;
      tutorExplanationBubble: string;
      slideImageCaption: string;
      slideImageAlt: string;
      tutorQuestionBubble: string;
      muteButtonLabel: string;
      endCallButtonLabel: string;
    };
  };
  examStyle: {
    heading: string;
    supportedExamsLine: string;
  };
  growTrees: {
    heading: string;
  };
  closingCallToAction: {
    heading: string;
    body: string;
    uploadSlidesButton: string;
    capybaraAndMonkeyIllustrationAlt: string;
  };
  callsToAction: {
    tryItFreeButton: string;
    // Also the label of the store buttons that replace the hero and closing calls to action for phone
    // visitors from a Meta ad (sections/AppStoreCallToAction.tsx).
    appStoreBadgeAlt: string;
    googlePlayBadgeAlt: string;
    // The short store link ("Get the app") in the nav and footer for those visitors.
    getTheAppButton: string;
    // Takes those visitors back to the web sign-up and upload.
    continueInBrowserButton: string;
  };
};

export type QualityChartMetricKey = keyof LandingCopy["qualityChart"]["metricNames"];

// The English values are the page's original strings, character for character.
export function getLandingCopy(language: LandingLanguage): LandingCopy {
  return getMarketingTranslations(language).landing as LandingCopy;
}

// Compatibility for existing bilingual consumers/tests; runtime resolves any locale on demand.
export const LANDING_COPY = { en: getLandingCopy("en"), es: getLandingCopy("es") };
